import React, { useEffect } from 'react';
import { useLazyGetProjectedJobsSummaryQuery } from '../api';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import DateUtils from 'utils/dateUtils';
import { ArrowRightIcon } from 'assets/icons';
import routesPath from 'routes/RoutesPath';
import { useNavigate } from 'react-router-dom';
import { JobsFilterType } from 'containers/jobs/constant';
import AmountPercentageToolTip from './AmountPercentageToolTip';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { GenericError, If } from 'components';
import Lottie from 'lottie-react';

const JobsOverallSummary = () => {
  const [getProjectedJobsSummaryQuery, projectedJobsSummaryState] = useLazyGetProjectedJobsSummaryQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const timezone = DateUtils.getTimezoneLongName();
  const navigate = useNavigate();

  const {
    data: projectedJobsSummaryData,
    isFetching: isFetchingProjectedJobsSummary,
    isError: isProjectedJobsSummaryError,
    isUninitialized: isProjectedJobsSummaryUninitialized
  } = projectedJobsSummaryState;
  const { futureProjection, pastProjection, overdueProjection, unscheduledProjection } =
    projectedJobsSummaryData?.data ?? {};

  useEffect(() => {
    if (currentTeam.id) {
      getProjectedJobsSummaryQuery({
        teamId: currentTeam.id,
        timezone
      });
    }
  }, [currentTeam.id]);

  const onViewOverdueJobsClick = () => {
    navigate(routesPath.JOBS, { state: { jobsFilters: { jobsFilterType: JobsFilterType.OVERDUE } } });
  };

  const onUnscheduledJobsClick = () => {
    navigate(routesPath.JOBS, { state: { jobsFilters: { jobsFilterType: JobsFilterType.UNSCHEDULED } } });
  };

  const onViewFutureJobsClick = () => {
    navigate(routesPath.JOBS, { state: { jobsFilters: { jobsFilterType: JobsFilterType.NEXT_7_DAYS } } });
  };

  return (
    <div className="mt-8 h-[360px] w-full rounded border border-borderGray p-5 lg:w-1/2">
      <div className="text-xl font-bold text-primaryText">Jobs</div>
      <If condition={isFetchingProjectedJobsSummary || isProjectedJobsSummaryUninitialized}>
        <div className="m-auto flex h-full items-center justify-center">
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </div>
      </If>
      {!isFetchingProjectedJobsSummary && projectedJobsSummaryData && (
        <div className="mt-6 ">
          <div className="border-b border-secondaryBtn pb-6">
            <div className="text-sbase font-semibold text-primaryText">Projected value of jobs in next 7 days </div>
            <div className="mt-2.5 flex items-center justify-between">
              <div className="flex items-center">
                <div className="text-xl font-bold text-tertiaryText">
                  {getAmountWithCurrency(futureProjection.amount)}
                </div>
                <AmountPercentageToolTip futureProjection={futureProjection} pastProjection={pastProjection} />
                <div className="ml-2 text-14px font-semibold text-accentText">
                  {`(${futureProjection.count}  ${futureProjection.count === 1 ? 'job' : 'jobs'})`}
                </div>
              </div>
              <div
                className={`flex cursor-pointer items-center text-px13 font-semibold text-secondary
                          ${futureProjection.count === 0 ? 'pointer-events-none opacity-50' : ''}`}
                onClick={onViewFutureJobsClick}>
                View
                <ArrowRightIcon className="ml-2 shrink-0" />
              </div>
            </div>
          </div>
          <div className="border-b border-secondaryBtn py-6">
            <div className="text-sbase font-semibold text-primaryText">Overdue jobs not completed</div>
            <div className="flex items-center justify-between">
              <div className="mt-2 flex items-center">
                <div className="mr-1.5 text-xl font-bold text-error">{overdueProjection.count}</div>
                <div className="text-14px font-semibold text-accentText">
                  {`Projected value of ${getAmountWithCurrency(overdueProjection.amount)}`}
                </div>
              </div>
              <div
                className={`flex cursor-pointer items-center text-px13 font-semibold text-secondary
                          ${overdueProjection.count === 0 ? 'pointer-events-none opacity-50' : ''}`}
                onClick={onViewOverdueJobsClick}>
                View
                <ArrowRightIcon className="ml-2 shrink-0" />
              </div>
            </div>
          </div>
          <div className="pt-6">
            <div className="text-sbase font-semibold text-primaryText">Unscheduled jobs</div>
            <div className="flex items-center justify-between">
              <div className="mt-2 flex items-center">
                <div className="mr-1.5 text-xl font-bold text-tertiaryText">{unscheduledProjection.count}</div>
                <div className="text-14px font-semibold text-accentText">
                  {`Projected value of ${getAmountWithCurrency(unscheduledProjection.amount)}`}
                </div>
              </div>
              <div
                className={`flex cursor-pointer items-center text-px13 font-semibold text-secondary
                          ${unscheduledProjection.count === 0 ? 'pointer-events-none opacity-50' : ''}`}
                onClick={onUnscheduledJobsClick}>
                View
                <ArrowRightIcon className="ml-2 shrink-0" />
              </div>
            </div>
          </div>
        </div>
      )}
      <If
        condition={
          isProjectedJobsSummaryError && !isFetchingProjectedJobsSummary && !isProjectedJobsSummaryUninitialized
        }>
        <GenericError showBackButton={false} />
      </If>
    </div>
  );
};

export default JobsOverallSummary;
